/**
 * consults.service.ts
 *
 * Typed API wrapper for the Consults worklist — the V5 port of the legacy
 * `inquiries_full_list-test.php` page (titled "Consults"). Uses the shared
 * apiClient and returns unwrapped envelope data, matching surgery.service.ts.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse } from '@/types/api.types';

// ─── Domain types ─────────────────────────────────────────────────────────────

/**
 * The row's colour, replacing the inline backgrounds legacy painted from the
 * stage name (dt-full-inquiry-list.php). The server decides; the client styles.
 *
 *  signedUp  — procedure or revision scheduled / completed, or not yet scheduled (green)
 *  cancelled — no show, cancelled consult, plan or procedure cancelled (red)
 *  pastDue   — follow-up whose last plan note is a week old or more (yellow)
 *  none      — everything else
 */
export type ConsultRowStatus = 'signedUp' | 'cancelled' | 'pastDue' | 'none';

export interface ConsultListItem {
  planId: number;
  patientId: number;
  planDisplayId: string | null;
  patientDisplayId: string | null;
  /** Pacific wall clock; null for a cancelled consult with no date. */
  consultDate: string | null;
  /** IANA zone of the plan's facility (else the practice's); Consult Date is shown in it. */
  facilityTimezone: string | null;
  patientName: string;
  facilityName: string;
  procedures: string;
  picsIn: boolean;
  planType: string;
  coordinatorName: string;
  /** The stage as legacy displayed it ("Consultation" reads "Follow-Up"). */
  stage: string;
  doctorName: string;
  /** Open tasks due today or later. */
  taskCount: number;
  due: number;
  /** The plan's sub-stage; empty when it has none. */
  inquiryStatus: string;
  /** 0–5 as stored; the list shows up to three stars. */
  interest: number;
  rowStatus: ConsultRowStatus;
}

/** The three search modes the legacy page offered. */
export type ConsultSearchType = 'name' | 'planId' | 'patientId';

/**
 * What the worklist sends, built by `buildConsultsListParams`
 * (pages/consults/consultsShared.ts). Filters travel inside `filtersArray`, the
 * JSON string every list page sends (lib/filterRegistry `buildApiParams`).
 */
export interface ConsultListParams {
  filtersArray: string;
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  searchType?: ConsultSearchType;
}

/** The summary cards, counted over every matching plan, not just the page. */
export interface ConsultSummary {
  pastDue: number;
  /** Rounded share of the total; null when the count is zero. */
  pastDuePercent: number | null;
  consultStage: number;
  consultStagePercent: number | null;
  signUps: number;
  signUpsPercent: number | null;
  cancelled: number;
  cancelledPercent: number | null;
  balance: number;
}

export interface ConsultListMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  sort: string | null;
  summary: ConsultSummary;
}

// ─── Calls ────────────────────────────────────────────────────────────────────

export function listConsults(
  params: ConsultListParams,
): Promise<PaginatedResponse<ConsultListItem> & { meta: ConsultListMeta }> {
  return apiClient
    .get<PaginatedResponse<ConsultListItem> & { meta: ConsultListMeta }>('/consults', { params })
    .then((r) => r.data);
}
